/**
 * Flow E's calls. Every one goes through `../../api/client.js` and nothing else.
 *
 * There is no `data.ts` behind these screens any more. E1–E4 render what the Worker
 * sends — including every Bangla string, because docs/05 § conventions puts the
 * localisation on the server and this module composes none of it.
 *
 * ── WHAT THIS MODULE DELIBERATELY DOES NOT HAVE ─────────────────────────────
 * No fallback fixture, no "if the API is unreachable, show the drawn values". A screen
 * that silently substitutes design data for live data is a screen that looks correct in
 * review and lies in production — which is the failure the whole wiring pass exists to
 * end. When a call fails the boundary renders a drawn STATE, which is a different thing:
 * it says something true about the failure.
 */
import type {
  ActivityChat,
  ActivityDetail,
  ActivityListResponse,
  ActivityTabId,
  SupportTicketRequest,
  SupportTicketResponse,
} from '@oprappo/contracts';
import { api } from '../../api/client.js';
// The browser half of the challenge. It lives under `onboarding/` because A3 is where it
// was first mounted; it is a module, not a screen, and the token it holds is the same one
// every guarded form sends. `routes/vendor/api.ts` reads it the same way for V1.
import { turnstileToken } from '../onboarding/turnstile.js';

/** E1. `tab` is sent as a query the server filters on — the client never filters a list. */
export function fetchActivities(
  tab: ActivityTabId,
  signal?: AbortSignal,
): Promise<ActivityListResponse> {
  return api.get<ActivityListResponse>(`/v1/activity?tab=${tab}`, { signal });
}

/** E2, and E4's panel with it. One read, so the two cannot disagree about the order. */
export function fetchActivity(id: string, signal?: AbortSignal): Promise<ActivityDetail> {
  return api.get<ActivityDetail>(`/v1/activity/${encodeURIComponent(id)}`, { signal });
}

/** E3. */
export function fetchChat(id: string, signal?: AbortSignal): Promise<ActivityChat> {
  return api.get<ActivityChat>(`/v1/activity/${encodeURIComponent(id)}/messages`, { signal });
}

/**
 * E3's composer.
 *
 * Answers with the WHOLE conversation, which is what the route returns: re-rendering
 * from server truth is what stops a locally-appended bubble disagreeing with the next
 * read. No `Idempotency-Key` — this moves no money, and `client.ts` never retries a POST,
 * so a duplicate can only come from a person tapping twice.
 */
export function sendChatMessage(id: string, bodyBn: string): Promise<ActivityChat> {
  return api.post<ActivityChat>(`/v1/activity/${encodeURIComponent(id)}/messages`, {
    body_bn: bodyBn,
  });
}

/**
 * E4. `problem_id` is one of the five the server draws; the client sends the id it was
 * given.
 *
 * ── THE CHALLENGE TRAVELS WITH IT ────────────────────────────────────────────
 * docs/09 § 62 names three surfaces for Turnstile — OTP request, vendor signup and
 * SUPPORT SUBMISSION — and this is the third. A ticket now reaches an operator's queue
 * (`apps/api/src/ops/**`), so a script that walks this form buries the real customer who
 * is waiting for an answer behind complaints about orders nobody placed.
 *
 * `turnstile_token` is read from the widget's own module and is sent ONLY when the
 * widget has produced one — never as an empty string, and never invented. A build with
 * no sitekey produces no token, and `turnstileToken()` answers null for both that and
 * "the widget has not answered yet", which the client is deliberately unable to tell
 * apart: the Worker decides what a token-less request means — it refuses when the
 * control is deployed and lets it through when it is not
 * (`apps/api/src/auth/turnstile.ts § the outage decision`). The client never makes that
 * decision.
 *
 * An explicit `undefined` is not sent either: the Worker's schema is `strictObject`, and
 * `turnstile_token: undefined` survives neither `JSON.stringify` nor review as an
 * intention. The field is present or it is absent.
 */
export function openSupportTicket(request: SupportTicketRequest): Promise<SupportTicketResponse> {
  const token = turnstileToken();
  return api.post<SupportTicketResponse>(
    '/v1/support/tickets',
    token === null ? request : { ...request, turnstile_token: token },
  );
}
